<script setup lang="ts">
/**
 * One image/video/app at the preview's playhead, as the player shows it: the full image (not its thumbnail)
 * with the item's fit, the real video file playing in step with the playhead (muted; paused = that frame),
 * apps through ZoneWidget. `width`/`height` are the section's canvas pixels (apps lay out at that size).
 */
import { onMounted, ref, watch } from 'vue';
import type { Fit, MediaAssetRecord } from '@codot/contracts';
import ZoneWidget from './ZoneWidget.vue';

const props = withDefaults(
  defineProps<{ asset: MediaAssetRecord; offsetMs: number; playing: boolean; width: number; height: number; fit?: Fit }>(),
  { fit: 'fit' },
);
const FIT: Record<Fit, string> = { fit: 'object-contain', fill: 'object-cover', stretch: 'object-fill' };

const video = ref<HTMLVideoElement | null>(null);
function sync(): void {
  const v = video.value;
  if (!v) return;
  const want = props.offsetMs / 1000;
  // Paused: show exactly the playhead's frame. Playing: let it run, correct only real drift (scrubbing).
  if (!props.playing || Math.abs(v.currentTime - want) > 0.4) v.currentTime = want;
  if (props.playing && v.paused) void v.play().catch(() => undefined);
  if (!props.playing && !v.paused) v.pause();
}
watch(() => [props.offsetMs, props.playing], sync);
onMounted(sync);
</script>

<template>
  <ZoneWidget v-if="asset.config" :config="asset.config" :playing="playing" :width="width" :height="height" />
  <video
    v-else-if="asset.kind === 'video' && asset.fileUrl"
    ref="video"
    :src="asset.fileUrl"
    :poster="asset.thumbnailUrl ?? undefined"
    class="h-full w-full bg-black"
    :class="FIT[fit]"
    muted
    playsinline
    preload="auto"
    @loadedmetadata="sync"
  />
  <img v-else-if="asset.fileUrl || asset.thumbnailUrl" :src="(asset.fileUrl ?? asset.thumbnailUrl)!" alt="" class="h-full w-full" :class="FIT[fit]" />
  <span v-else class="flex h-full w-full items-center justify-center text-2xl" aria-hidden="true">{{ asset.kind === 'video' ? '🎬' : asset.kind === 'app' ? '🧩' : '🖼️' }}</span>
</template>
